import * as THREE from 'three';
import { drawPolylineFromCode } from '../../commands/drawPolylineFromCode';
import { createSolidHatchFromBoundary } from '../../commands/createSolidHatchFromBoundary';
import { generateUniqueId } from '../../utils/generateUniqueId';
import { drawCircleFromCode } from '../../commands/drawCircleFromCode';
import { createTextSprite } from '../../utils/createTextSprite';

export function createPanel(scene, mousePos, dir, symmetricalOffset, isPreview = false, panelKatPlanNo, panelInfo = "") {
  const group = new THREE.Group();
  group.name = "panel";
  const panelID = generateUniqueId('panel');

  const offsetGroup = new THREE.Group();
  offsetGroup.name = "offsetGroup";

  const panelWidth = dir * 15;
  const panelHeight = 25;

  const basePoint = {
    x: mousePos.x,
    y: mousePos.y,
  };
  group.position.set(basePoint.x, basePoint.y, 0);
  offsetGroup.position.set(0, 0, 0);
  group.add(offsetGroup);

  const halfW = panelWidth / 2;
  const halfH = panelHeight / 2;
  const providedColor = panelInfo && typeof panelInfo === 'object' && panelInfo.color ? panelInfo.color : null;
  const defaultColor = 0xff0000;
  const symbolColor = providedColor ?? defaultColor;

  // === 1. Polyline ===
  const polyline = drawPolylineFromCode(scene, {
    points: [
      { x: -halfW + symmetricalOffset * dir, y: -halfH },
      { x: -halfW + symmetricalOffset * dir, y: halfH },
      { x: halfW + symmetricalOffset * dir, y: halfH },
      { x: halfW + symmetricalOffset * dir, y: -halfH },
    ],
    color: symbolColor,
    closed: true,
  });

  if (polyline) {
    polyline.userData = { isPanelPart: true, panelID };
    polyline.name = 'panel-outer';
    offsetGroup.add(polyline);
  }

  // === 2. Hatch ===
  const hatchElement = {
    handle: generateUniqueId(),
    SolidFillFlag: 1,
    HatchPatternName: "SOLID",
  };

  const hatchPaths = [
    {
      type: "outer",
      groups: [
        {
          edgeType: "Line",
          x1: -halfW + symmetricalOffset * dir,
          y1: halfH,
          x2: halfW + symmetricalOffset * dir,
          y2: -halfH
        },
        {
          edgeType: "Line",
          x1: halfW + symmetricalOffset * dir,
          y1: -halfH,
          x2: -halfW + symmetricalOffset * dir,
          y2: -halfH
        }
      ]
    }
  ];

  const hatch = createSolidHatchFromBoundary(hatchElement, {
    boundaryPaths: hatchPaths,
    color: symbolColor,
    scene: scene,
    group: offsetGroup,
  });

  if (hatch) {
    hatch.name = 'panel-hatch';
    hatch.userData = { ...hatch.userData, isPanelPart: true, panelID };
    offsetGroup.add(hatch);
  }

  // === 3. Front edge ===
  const frontEdgePoints = [
    new THREE.Vector3(halfW + symmetricalOffset * dir, -halfH, 0),
    new THREE.Vector3(halfW + symmetricalOffset * dir, halfH, 0)
  ];

  const frontEdgeGeometry = new THREE.BufferGeometry().setFromPoints(frontEdgePoints);
  const frontEdgeMaterial = new THREE.LineBasicMaterial({ 
    color: symbolColor,
    linewidth: 2
  });
  const frontEdgeLine = new THREE.Line(frontEdgeGeometry, frontEdgeMaterial);

  frontEdgeLine.name = 'panel-front-edge';
  frontEdgeLine.userData = {
    isPanelPart: true,
    panelID,
    lineType: 'frontEdge'
  };

  offsetGroup.add(frontEdgeLine);

  // === 4. Transparan Tıklama Hatch'i ===
  const clickHatchElement = {
    handle: generateUniqueId(),
    SolidFillFlag: 1,
    HatchPatternName: "SOLID",
  };

  const clickHatchPaths = [
    {
      type: "outer",
      groups: [
        {
          edgeType: "Line",
          x1: -halfW + symmetricalOffset * dir,
          y1: -halfH,
          x2: -halfW + symmetricalOffset * dir,
          y2: halfH
        },
        {
          edgeType: "Line",
          x1: -halfW + symmetricalOffset * dir,
          y1: halfH,
          x2: halfW + symmetricalOffset * dir,
          y2: halfH
        },
        {
          edgeType: "Line",
          x1: halfW + symmetricalOffset * dir,
          y1: halfH,
          x2: halfW + symmetricalOffset * dir,
          y2: -halfH
        }
      ]
    }
  ];

  const clickHatch = createSolidHatchFromBoundary(clickHatchElement, {
    boundaryPaths: clickHatchPaths,
    color: 0x000000,
    opacity: 0.01,
    depthWrite: false,
    transparency: true,
    group: offsetGroup,
  });

  if (clickHatch) {
    clickHatch.name = 'panel-click-hatch';
    clickHatch.userData = {
      ...clickHatch.userData,
      isPanelPart: true,
      panelID,
      originalColor: 0x000000,
      originalOpacity: 0.01,
      originalTransparent: true
    };

    // Manuel olarak transparency ayarlarını uygula
    if (clickHatch.material) {
      clickHatch.material.opacity = 0.01;
      clickHatch.material.transparent = true;
      // colorWrite=false: tıklama alanı asla boyanmaz (raycast çalışır).
      clickHatch.material.colorWrite = false;
      clickHatch.material.needsUpdate = true;
    }

    offsetGroup.add(clickHatch);
  }

  group.userData = {
    id: panelID,

    type: 'electricalPanel',
    function: 'electricalPanel',
    name: panelInfo.name,
    panelKatPlanNo,
    isSelectable: true,
    isPanelPreview: true,
    originalColor: 0xffffff,
    entries: [
      {
        id: generateUniqueId('entry'),
        type: 'breaker',
        label: 'Ana Sigorta',
        MCB_No: 435, // 25A, 1P, B curve
        phases: 3
      },
      {
        id: generateUniqueId('entry'),
        type: 'rcd',
        label: 'Kaçak Akım Rölesi',
        RCD_No: 0, // RCD array index
        sensitivity: '30mA',
        phases: 3
      }
    ],
    outs: [],
    outSnaps: [],
    basePoint: { ...basePoint },
    symmetricalOffset,
    height: panelHeight,
    startH: panelHeight / 2,
    endH: panelHeight / 2 - 5,
    dir,
    details: {
      kullanimAmac: (panelInfo && panelInfo.purposeOfUse) || "Mesken",
      aboneTipi: (panelInfo && panelInfo.aboneTuru) || "Mesken",
      kuruluGuc: 5,
      talepFak: "60-40%",
      talepGuc: 5,
      faz: "rst",
      akim: 5,
    },
    salt: {
      kesici: { no: 7, Etiket: "4xB-25A" },
      kolon: { no: 7, Etiket: "4x4mm2 NYM" },
      KAKR: { no: 9, Etiket: "4P 30mA 25A" },
      SayacKesici: { no: 9, Etiket: "4xC-25A" }
    }
  };

  // Panel Title ekleme - supply point style ile.
  // Önizleme (sürükleme) sırasında da panelInfo.name verildiyse adı göster
  // (kullanıcı kat-temelli auto-name'in sürüklerken oluştuğunu görmek istiyor).
  if (panelInfo && (panelInfo.name || !isPreview)) {
    const { name = "Panel", installedPower = 0 } = panelInfo;

    // İki satırlı title oluştur - supply point style ile
    const line2 = name;
    const line1 = `${installedPower}W`;

    // Title Group - Dragging için parent grup
    const titleGroup = new THREE.Group();
    titleGroup.name = "titleGroup";
    // Konumlandırmayı mainPanel ile aynı mantıkla yap: dir'e göre yer değiştirir
    // Böylece panel 0/180 derecede hizalı kalır, 90/270 derecede üst üste binmeyi engeller
    titleGroup.position.set(0, (panelHeight / 2 + 16) * dir, 0.1);

    // İlk satır (Panel adı) - supply point gibi büyük font
    // NOTE: Temporarily disabled power text (commented out) to avoid duplicate/small sprites
    /*
    const titleSprite1 = createTextSprite(line1, {
      fontSize: 75,
      color: '#ffffff',
      backgroundColor: 'transparent',
      strokeColor: 'black',
      padding: 4
    });
    titleSprite1.position.set(4*dir, -4*dir, 0);
    titleSprite1.name = "title_"+name;
    // mark role so updaters can reliably find which sprite is power vs name
    titleSprite1.userData = { ...(titleSprite1.userData || {}), role: 'power' };
    */

    // İkinci satır (Kurulu güç) - biraz daha küçük
    const titleSprite2 = createTextSprite(line2, {
      fontSize: 75,
      color: '#ffffff',
      backgroundColor: 'transparent',
      strokeColor: 'black',
      padding: 4
    });
    titleSprite2.position.set(-4*dir, 4*dir, 0);
    titleSprite2.name = "panelTitle2";

  // Title'ları group'a ekle (power text disabled for now)
  // only add the defined name/title sprite
  titleSprite2.userData = { ...(titleSprite2.userData || {}), role: 'name' };
  titleGroup.add(titleSprite2);

    // Dragging userData'sı
    titleGroup.userData = {
      isDraggable: true,
      panelCenter: new THREE.Vector3(0, 0, 0), // Panel merkezi (offsetGroup koordinatlarında)
      maxDistance: 110, // ✅ Maksimum sürükleme mesafesi 110 birim
      isTitle: true,
      titleType: 'panel',
      // linkedSprites: only keep the name sprite for now (power disabled)
      linkedSprites: [titleSprite2]
    };

    offsetGroup.add(titleGroup);  
  }

  if (scene) {
    scene.add(group);
  }
  return group;
}
